import { Module } from 'vuex';
import { AmountOption } from '../../../../module/Deals/view/vue/components/banner/form/types';
import { ICity } from '../../../../module/SearchBar/view/vue/domain/City';
import SearchBarService from '../../../services/SearchBarService';

export type DisplayValue = {
  mobile?: string;
  desktop?: string;
};

export type DateValue = {
  date?: string;
  dateShortcut?: string;
  fromDate?: string;
  toDate?: string;
};

export type SearchBannerState = {
  image?: string;
  cityUnique?: string;
  city: {
    displayValue?: DisplayValue;
    data?: ICity;
  };
  date: {
    displayValue?: DisplayValue;
    data?: DateValue;
  };
  amount: {
    displayValue?: DisplayValue;
    data?: number;
    options?: AmountOption[];
  };
  loading: boolean;
  refetching: boolean;
};

export type SearchBannerConfig = {
  apiType: string;
  amountLabelField: string;
  amountOptionsField: string;
  amountValueField: {
    getResponse: string;
    postRequest: string;
    postResponse: string;
  };
};

export function createSearchBannerModule(
  config: SearchBannerConfig
): Module<SearchBannerState, any> {
  return {
    namespaced: true,
    state: () => ({
      image: undefined,
      cityUnique: undefined,
      city: {
        displayValue: {
          mobile: undefined,
          desktop: undefined,
        },
        data: undefined,
      },
      date: {
        displayValue: {
          mobile: undefined,
          desktop: undefined,
        },
        data: undefined,
      },
      amount: {
        displayValue: {
          mobile: undefined,
          desktop: undefined,
        },
        data: undefined,
        options: [],
      },
      loading: false,
      refetching: false,
    }),
    mutations: {
      setImage(state, image: string) {
        state.image = image;
      },
      setCityUnique(state, cityUnique: string) {
        state.cityUnique = cityUnique;
      },
      setCity(state, payload: Partial<SearchBannerState['city']>) {
        state.city = { ...state.city, ...payload };
      },
      setDate(state, payload: Partial<SearchBannerState['date']>) {
        state.date = { ...state.date, ...payload };
      },
      setAmount(state, payload: Partial<SearchBannerState['amount']>) {
        state.amount = { ...state.amount, ...payload };
      },
      setLoading(state, loading: boolean) {
        state.loading = loading;
      },
      setRefetching(state, refetching: boolean) {
        state.refetching = refetching;
      },
      resetState(state) {
        state.city = {
          displayValue: {
            mobile: undefined,
            desktop: undefined,
          },
          data: undefined,
        };
        state.date = {
          displayValue: {
            mobile: undefined,
            desktop: undefined,
          },
          data: undefined,
        };
        state.amount = {
          displayValue: {
            mobile: undefined,
            desktop: undefined,
          },
          data: undefined,
          options: [],
        };
        state.loading = false;
        state.refetching = false;
      },
    },
    getters: {
      image: (state) => {
        if (!state.image) {
          return '';
        }

        if (state.image.startsWith('http')) {
          return state.image;
        }

        return `https://images.socialdeal.nl${state.image}`;
      },

      // City getters
      cityUnique: (state) => state.cityUnique,
      cityDisplayValue: (state) => state.city.displayValue,
      cityDisplayValueMobile: (state) => state.city.displayValue?.mobile,
      cityDisplayValueDesktop: (state) => state.city.displayValue?.desktop,
      cityData: (state) => state.city.data,

      // Date getters
      dateDisplayValue: (state) => state.date.displayValue,
      dateDisplayValueMobile: (state) => state.date.displayValue?.mobile,
      dateDisplayValueDesktop: (state) => state.date.displayValue?.desktop,
      dateData: (state) => state.date.data,

      // Amount getters
      amountDisplayValue: (state) => state.amount.displayValue,
      amountDisplayValueMobile: (state) => state.amount.displayValue?.mobile,
      amountDisplayValueDesktop: (state) => state.amount.displayValue?.desktop,
      amountData: (state) => state.amount.data,
      amountOptions: (state) => state.amount.options,

      // Loading getter
      isLoading: (state) => state.loading,
      isRefetching: (state) => state.refetching,
    },
    actions: {
      async fetchInitialData({ commit }, cityUnique: string) {
        commit('setLoading', true);
        commit('setCityUnique', cityUnique);

        try {
          const data = await SearchBarService.getSearchBarByType(config.apiType, cityUnique);

          if (data?.image) {
            commit('setImage', data.image);
          }

          if (data?.search_bar) {
            commit('setCity', {
              displayValue: {
                mobile: data.search_bar?.title,
                desktop: data.search_bar?.title,
              },
              data: {
                slug: data.search_bar?.slug,
                location: data.search_bar?.location,
                latitude: data.search_bar?.latitude,
                longitude: data.search_bar?.longitude,
              },
            });
          }

          const datePayload: Partial<SearchBannerState['date']> = {};
          if (data?.date_label) {
            datePayload.displayValue = {
              mobile: data.date_label.mobile,
              desktop: data.date_label.desktop,
            };
          }

          if (data?.search_bar) {
            datePayload.data = {
              date: data.search_bar.date,
              dateShortcut: data.search_bar.date_shortcut,
              fromDate: data.search_bar.from_date,
              toDate: data.search_bar.till_date,
            };
          }

          if (Object.keys(datePayload).length > 0) {
            commit('setDate', datePayload);
          }

          const amountPayload: Partial<SearchBannerState['amount']> = {};
          if (data?.[config.amountLabelField]) {
            amountPayload.displayValue = {
              mobile: data[config.amountLabelField].mobile,
              desktop: data[config.amountLabelField].desktop,
            };
          }

          if (data?.[config.amountOptionsField]) {
            amountPayload.options = data[config.amountOptionsField].map((option: any) => ({
              value: Number(option.value_amount),
              label: option.value,
            }));
          }

          if (data?.search_bar?.[config.amountValueField.getResponse]) {
            amountPayload.data = data.search_bar[config.amountValueField.getResponse];
          }

          if (Object.keys(amountPayload).length > 0) {
            commit('setAmount', amountPayload);
          }
        } catch (error) {
          console.error('Error fetching search banner data:', error);
        } finally {
          commit('setLoading', false);
        }
      },
      async updateData({ commit, state }) {
        commit('setRefetching', true);

        try {
          const cityData = state.city.data;
          const dateData = state.date.data;

          const data = await SearchBarService.updateSearchBar(config.apiType, state.cityUnique, {
            slug: cityData.slug,
            location: cityData.location,
            longitude: cityData.longitude,
            latitude: cityData.latitude,
            date: dateData.date,
            date_shortcut: dateData.dateShortcut,
            from_date: dateData.fromDate,
            till_date: dateData.toDate,
            [config.amountValueField.postRequest]: state.amount.data,
          });

          if (data?.search_bar_data?.location) {
            commit('setCity', {
              displayValue: {
                mobile: data.search_bar_data.location,
                desktop: data.search_bar_data.location,
              },
            });
          }

          if (data?.input_data?.date_label) {
            commit('setDate', {
              displayValue: {
                mobile: data.input_data.date_label.mobile,
                desktop: data.input_data.date_label.desktop,
              },
            });
          }

          const amountPayload: Partial<SearchBannerState['amount']> = {};
          if (data?.input_data?.[config.amountLabelField]) {
            amountPayload.displayValue = {
              mobile: data.input_data[config.amountLabelField].mobile,
              desktop: data.input_data[config.amountLabelField].desktop,
            };
          }

          if (data?.search_bar_data?.[config.amountValueField.postResponse]) {
            amountPayload.data = data.search_bar_data[config.amountValueField.postResponse];
          }

          if (Object.keys(amountPayload).length > 0) {
            commit('setAmount', amountPayload);
          }
        } finally {
          commit('setRefetching', false);
        }
      },
      async updateCity({ commit, dispatch }, city: ICity) {
        commit('setCity', {
          data: city,
        });
        await dispatch('updateData');
      },
      async updateDate({ commit, dispatch }, date: DateValue) {
        commit('setDate', {
          data: {
            date: undefined,
            dateShortcut: undefined,
            fromDate: undefined,
            toDate: undefined,
            ...date,
          },
        });
        await dispatch('updateData');
      },
      async clearDate({ commit, dispatch }) {
        commit('setDate', {
          data: {
            date: undefined,
            dateShortcut: undefined,
            fromDate: undefined,
            toDate: undefined,
          },
        });
        await dispatch('updateData');
      },
      async updateAmount({ commit, dispatch }, amount: number) {
        commit('setAmount', {
          data: amount,
        });
        await dispatch('updateData');
      },
    },
  };
}
